import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import Swal from 'sweetalert2';
import { ArrowLeftIcon, BuildingOffice2Icon, XMarkIcon, IdentificationIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

import Footer from '../components/footer';
import PopoverComponentCustom from '../components/popover_component';
import ComboboxAutocomplete from '../components/combobox_autocomplete';

import { AuthContext } from '../contexts/auth';
import { listOrganizationalUnits } from '../services/api_org_unit';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { createJustificationProfile, getJustificationProfileDetail, updateJustificationProfile } from '../services/api_justification_profiles';
import { v2EstraiMessaggiErrore } from '../services/utils';


export default function JustificationProfileForm() {
  const { token } = useContext(AuthContext);
  const [, setLocation] = useLocation();

  const [, params] = useRoute('/justification_profile/:id');

  const profileId = params?.id || null;
  const isEdit = Boolean(profileId);

  const [name, setName] = useState('');

  const [giustificativi, setGiustificativi] = useState([]);
  const [selectedJustifications, setSelectedJustifications] = useState([]);

  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);

  const [nameError, setNameError] = useState('');
  const [justificationsError, setJustificationsError] = useState('');
  const [generalError, setGeneralError] = useState('');

  const nameSectionRef = useRef(null);
  const justificationsSectionRef = useRef(null);


  /* =========================================================
    LOAD DATA
  ========================================================= */
  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      setGeneralError('');

      try {
        const requests = [
          listOrganizationalUnits(
            token,
            true,
            null,
            'shifts'
          ),
          getJustificationsCompanyList(token, null, true),
        ];

        /*
         * In modifica carichiamo il singolo profilo.
         * In creazione questa chiamata non viene effettuata.
         */
        if (isEdit) {
          requests.push(getJustificationProfileDetail(token, profileId));
        }

        const [unitsResponse, justificationsResponse, profileResponse ] = await Promise.all(requests);

        if (!isMounted) return;


        /* -------------------------
           ORGANIZATIONAL UNITS
        ------------------------- */
        const loadedUnits = unitsResponse?.data || [];
        setUnits(loadedUnits);

        /* -------------------------
           JUSTIFICATIONS
        ------------------------- */
        const justifications = Array.isArray(justificationsResponse) ? [...justificationsResponse] : [];
        justifications.sort((a, b) => (a.justification?.code || '').localeCompare(b.justification?.code || ''));
        setGiustificativi(justifications);

        /* =============================================
           EDIT - PRECOMPILE
        ============================================= */
        if (isEdit && profileResponse) {
          /* NAME */
          setName(profileResponse.name || '');
          /* JUSTIFICATIONS */
          const selectedJustificationIds = new Set((profileResponse.justifications || []).map((item) => item.id));
          setSelectedJustifications(justifications.filter((item) => selectedJustificationIds.has(item.id)));
          /* ORGANIZATIONAL UNITS */
          const selectedUnitIds = new Set((profileResponse.organizational_units || []).map((unit) => unit.id));
          setSelectedUnits(loadedUnits.filter((unit) => selectedUnitIds.has(unit.id)));
        }
      } catch (error) {
        console.error('Error loading justification profile data:', error);

        if (isMounted && error?.response?.status !== 402) {
          setIsError(true);
          setGeneralError(v2EstraiMessaggiErrore(error));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [token, isEdit, profileId]);

  /* =========================================================
    JUSTIFICATIONS
  ========================================================= */
  const handleJustificationToggle = (justification) => {
    setSelectedJustifications((prev) =>
      prev.some((item) => item.id === justification.id)? prev.filter(
        (item) => item.id !== justification.id
      ) : [...prev, justification]
    );

    setJustificationsError('');
  };


  /* =========================================================
    ORGANIZATIONAL UNITS
  ========================================================= */
  const handleSelectUnit = (unit) => {
    if (!unit) return;

    setSelectedUnits((prev) => prev.some((item) => item.id === unit.id) ? prev : [...prev, unit]);
    setSelectedUnit(null);
  };

  /* =========================================================
    ERRORS
  ========================================================= */
  const scrollToError = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 0);
  };

  /* =========================================================
    SUBMIT
  ========================================================= */
  const handleSubmit = async (event) => {
    event.preventDefault();

    setNameError('');
    setJustificationsError('');
    setGeneralError('');

    if (!name.trim()) {
      setNameError('Inserisci il nome del profilo');

      scrollToError(nameSectionRef);
      return;
    }

    setIsSaving(true);

    const data = {
      name: name.trim(),
      justifications: selectedJustifications.map((item) => item.id),
      organizational_units: selectedUnits.map((item) => item.id),
    };

    try {
      Swal.fire({
        title: isEdit ? 'Salvataggio in corso...' : 'Creazione in corso...',
        text: isEdit ? 'Attendi mentre salviamo le modifiche al profilo' : 'Attendi mentre creiamo il nuovo profilo',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {Swal.showLoading();},
      });

      if (isEdit) {
        await updateJustificationProfile(token, profileId, data, 'justifications');
      } else {
        await createJustificationProfile(token, data, 'justifications');
      }

      await Swal.fire({
        title: isEdit ? 'Profilo aggiornato' : 'Profilo creato',
        text: isEdit ? 'Le modifiche sono state salvate correttamente' : 'Il nuovo profilo giustificativi è stato creato correttamente',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#4f46e5',
      });

      setLocation('/settings_ratei?tab=justification_profiles');

    } catch (error) {
      Swal.close();

      console.error(isEdit ? 'Errore nella modifica del profilo:' : 'Errore nella creazione del profilo:', error);

      const responseData = error?.response?.data;

      if (responseData?.name) {
        setNameError(Array.isArray(responseData.name) ? responseData.name.join(' ') : responseData.name);
        scrollToError(nameSectionRef);
      } else if (responseData?.justifications) {
        setJustificationsError(Array.isArray(responseData.justifications) ? responseData.justifications.join(' ') : responseData.justifications);
        scrollToError(justificationsSectionRef);
      } else {
        setGeneralError(v2EstraiMessaggiErrore(error));
      }
    } finally {
      setIsSaving(false);
    }
  };

  /* =========================================================
    RENDER
  ========================================================= */
  return (
    <div className="flex min-h-screen flex-col">

      <PopoverComponentCustom title={isEdit ? 'Modifica Profilo Giustificativi' : 'Nuovo Profilo Giustificativi'} />
      <main className="mt-10 flex-grow pb-8">
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">

          {/* BACK */}
          <Link href="/settings_ratei" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-indigo-600">
            <ArrowLeftIcon className="h-4 w-4" />Torna alla configurazione parametri
          </Link>

          <form onSubmit={handleSubmit} className="overflow-visible rounded-lg border border-gray-200 bg-white shadow-sm">
            {/* HEADER */}
            <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
              <h3 className="text-base font-semibold uppercase text-indigo-800">
                {isEdit ? 'Modifica profilo giustificativi' : 'Nuovo profilo giustificativi'}
              </h3>
              <p className="text-xs text-gray-500">
                Crea un gruppo di giustificativi e assegnalo alle unità organizzative
              </p>
            </div>

            {/* LOADING */}
            {isLoading && (
              <div className="border-b border-gray-200 px-5 py-8 text-center sm:px-6">
                <p className="text-sm text-gray-500">
                  {isEdit ? 'Caricamento profilo...' : 'Caricamento dati...'}
                </p>
              </div>
            )}

            {!isLoading && (
              <>
                {/* NOME PROFILO */}
                <div ref={nameSectionRef}>
                  <Section
                    title="Nome profilo"
                    description="Inserisci un nome che permetta di identificare facilmente il profilo"
                    icon={IdentificationIcon}
                  >
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {setName(e.target.value); setNameError('');}}
                      placeholder="Es. Gruppo Amministrativi, Gruppo Edili..."
                      className={`mt-1.5 block w-full rounded-md bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                        nameError ? 'border border-red-400 focus:border-red-500 focus:ring-red-500' : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                      }`}
                    />
                    {nameError && (
                      <p className="mt-2 text-xs text-red-600">
                        {nameError}
                      </p>
                    )}
                  </Section>
                </div>


                {/* GIUSTIFICATIVI */}
                <div ref={justificationsSectionRef}>
                  <Section
                    title="Giustificativi"
                    description='Seleziona i giustificativi che devono appartenere a questo profilo. Vengono mostrati in questa sezione solo i giustificativi che sono stati abilitati nella sezione "Giustificativi" in Configurazione Parametri. '
                    count={selectedJustifications.length}
                    icon={ClipboardDocumentListIcon}
                  >
                    {giustificativi.length > 0 ? (
                      <div className="overflow-hidden rounded-md border border-gray-200">
                        <div className="grid grid-cols-1 gap-px bg-gray-50 lg:grid-cols-2">
                          {giustificativi.map(
                            (item) => {
                              const checked = selectedJustifications.some((selected) => selected.id === item.id);
                              return (
                                <label
                                  key={item.id}
                                  className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors ${checked ? 'bg-indigo-50' : 'bg-gray-50'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleJustificationToggle(item)}
                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <span className="min-w-[65px] text-sm font-semibold text-gray-800">
                                    {item.justification?.code}
                                  </span>
                                  <span className="min-w-0 truncate text-sm text-gray-500">
                                    {item.justification?.description}
                                  </span>
                                </label>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                        Nessun giustificativo disponibile
                      </div>
                    )}

                    {justificationsError && (<p className="mt-2 text-xs text-red-600">{justificationsError}</p>)}

                    {selectedJustifications.length > 0 && (
                      <ClearSelection
                        count={selectedJustifications.length}
                        label="giustificativi"
                        onClear={() => setSelectedJustifications([])}
                      />
                    )}
                  </Section>
                </div>

                {/* UNITÀ ORGANIZZATIVE */}
                <Section
                  title="Unità organizzative"
                  description="Assegna il profilo a una o più unità organizzative"
                  count={selectedUnits.length}
                  icon={BuildingOffice2Icon}
                >
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Aggiungi unità organizzativa
                  </label>

                  <ComboboxAutocomplete
                    options={units}
                    value={selectedUnit}
                    onChange={handleSelectUnit}
                    placeholder="Seleziona unità organizzativa..."
                    bgActiveColor="bg-indigo-50"
                    textActiveColor="text-gray-700"
                    positionOptions="absolute"
                    zIndex="z-[1500]"
                    optionsClassName="border border-gray-300 max-h-36 rounded-lg"
                    openDirection="top"
                    hideXMarkIcon
                    borderInput="border border-gray-200 rounded-lg"
                  />

                  {selectedUnits.length > 0 && (
                    <>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedUnits.map((unit) => (
                            <span key={unit.id} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-2.5 py-1.5 text-xs font-medium text-indigo-800">
                              {unit.name}
                              <button
                                type="button"
                                onClick={() => setSelectedUnits((prev) => prev.filter((item) => item.id !== unit.id))}
                                className="text-gray-400 hover:text-gray-700"
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )
                        )}
                      </div>

                      <ClearSelection
                        count={selectedUnits.length}
                        label="unità organizzative"
                        onClear={() => {setSelectedUnits([]); setSelectedUnit(null);}}
                      />
                    </>
                  )}
                </Section>

                {/* GENERAL ERROR */}
                {generalError && (
                  <div className="border-t border-red-200 bg-red-50 px-5 py-3 sm:px-6">
                    <p className="text-xs text-red-700">
                      {generalError}
                    </p>
                  </div>
                )}


                {/* FOOTER FORM */}
                <div className="flex justify-center gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:px-6">
                  <Link
                    href="/settings_ratei?tab=justification_profiles"
                    className="rounded-md bg-white px-4 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 w-40"
                  >
                    Annulla
                  </Link>

                  <button
                    type="submit"
                    disabled={isSaving || isLoading}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-300 disabled:cursor-not-allowed disabled:opacity-50 w-40"
                  >
                    {isSaving
                      ? isEdit ? 'Salvataggio...' : 'Creazione...'
                      : isEdit ? 'Salva modifiche' : 'Crea profilo'}
                  </button>
                </div>
              </>
            )}
          </form>

          {isError && !generalError && (
            <p className="mt-4 text-center text-sm text-red-600">
              Si è verificato un errore durante il caricamento dei dati
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* =========================================================
  INTERNAL UI
========================================================= */
function Section({ title, description, count = 0, icon: Icon, children }) {
  return (
    <section className="border-b border-gray-200 last:border-b-0">
      <div className="flex items-start justify-between gap-4 bg-indigo-50 px-5 py-2 sm:px-6">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-indigo-300 bg-indigo-100">
              <Icon className="h-4 w-4 text-indigo-600" />
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold text-indigo-800">
              {title}
            </h3>
            {description && (
              <p className="mt-0.5 text-xs text-gray-500">
                {description}
              </p>
            )}
          </div>
        </div>

        {count > 0 && (
          <span className="shrink-0 rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
            {count} selezionati
          </span>
        )}
      </div>

      <div className="bg-white px-5 pb-6 pt-4 sm:px-6">
        {children}
      </div>
    </section>
  );
}

/* =========================================================
  CLEAR SELECTION
========================================================= */
function ClearSelection({ count, label, onClear }) {
  return (
    <div className="mt-3 flex items-center justify-between px-1">
      <span className="text-[11px] text-gray-400">
        {count} {label} selezionati
      </span>
      <button
        type="button"
        onClick={onClear}
        className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
      >
        Deseleziona tutti
      </button>
    </div>
  );
}